import { dialogActionTranslations } from "./dialogActionTranslations";
import { createContext, useContext } from "react";
const LANGUAGE_CODE_LENGTH = 2;

/**
 * Texte der Kit-Bausteine (Schaltflächen ohne sichtbare Beschriftung, Ladeanzeige). Das Kit ist
 * antd-kompatibel und bekommt deshalb keine `tr`-Props; die Sprache setzt das Produkt einmal über
 * `UiLanguageProvider`. Ohne Provider gilt `<html lang>`, sonst Deutsch.
 */
const uiTextDe = {
  ...dialogActionTranslations.de,
  Close: "schließen",
  Clear: "leeren",
  Search: "suchen",
  "Show password": "Passwort anzeigen",
  "Hide password": "Passwort verbergen",
  "Loading...": "wird geladen …",
  Copy: "kopieren",
  Copied: "kopiert",
  more: "mehr",
  active: "aktiv",
  inactive: "inaktiv",
  Actions: "Aktionen",
  Pages: "Seiten",
  "Previous page": "vorherige Seite",
  "Next page": "nächste Seite",
  "Page {page}": "Seite {page}",
  OK: "OK",
  Cancel: "abbrechen",
  Remove: "entfernen",
  "No data": "keine Daten",
  Today: "heute",
  Now: "jetzt",
  "Previous year": "voriges Jahr",
  "Next year": "nächstes Jahr", "Choose period": "Zeitraum wählen",
};
export type UiTextKey = keyof typeof uiTextDe;

const uiText: Record<string, Record<UiTextKey, string>> = {
  de: uiTextDe,
  en: { ...dialogActionTranslations.en, Close: "Close", Clear: "Clear", Search: "Search", "Show password": "Show password", "Hide password": "Hide password", "Loading...": "Loading...", Copy: "Copy", Copied: "Copied", more: "more", active: "active", inactive: "inactive", Actions: "Actions", Pages: "Pages", "Previous page": "Previous page", "Next page": "Next page", "Page {page}": "Page {page}", OK: "OK", Cancel: "Cancel", Remove: "Remove", "No data": "No data", Today: "Today", Now: "Now", "Previous year": "Previous year", "Next year": "Next year", "Choose period": "Choose period" },
  ru: { ...dialogActionTranslations.ru, Close: "закрыть", Clear: "очистить", Search: "искать", "Show password": "показать пароль", "Hide password": "скрыть пароль", "Loading...": "загрузка …", Copy: "копировать", Copied: "скопировано", more: "ещё", active: "активно", inactive: "неактивно", Actions: "Действия", Pages: "страницы", "Previous page": "предыдущая страница", "Next page": "следующая страница", "Page {page}": "страница {page}", OK: "ОК", Cancel: "отмена", Remove: "удалить", "No data": "нет данных", Today: "сегодня", Now: "сейчас", "Previous year": "предыдущий год", "Next year": "следующий год", "Choose period": "Выбрать период" },
  uk: { ...dialogActionTranslations.uk, Close: "закрити", Clear: "очистити", Search: "шукати", "Show password": "показати пароль", "Hide password": "приховати пароль", "Loading...": "завантаження …", Copy: "копіювати", Copied: "скопійовано", more: "ще", active: "активно", inactive: "неактивно", Actions: "Дії", Pages: "сторінки", "Previous page": "попередня сторінка", "Next page": "наступна сторінка", "Page {page}": "сторінка {page}", OK: "ОК", Cancel: "скасувати", Remove: "видалити", "No data": "немає даних", Today: "сьогодні", Now: "зараз", "Previous year": "попередній рік", "Next year": "наступний рік", "Choose period": "Вибрати період" },
  it: { ...dialogActionTranslations.it, Close: "chiudi", Clear: "svuota", Search: "cerca", "Show password": "mostra password", "Hide password": "nascondi password", "Loading...": "caricamento …", Copy: "copia", Copied: "copiato", more: "altro", active: "attivo", inactive: "inattivo", Actions: "Azioni", Pages: "pagine", "Previous page": "pagina precedente", "Next page": "pagina successiva", "Page {page}": "pagina {page}", OK: "OK", Cancel: "annulla", Remove: "rimuovi", "No data": "nessun dato", Today: "oggi", Now: "adesso", "Previous year": "anno precedente", "Next year": "anno successivo", "Choose period": "Scegli periodo" },
};

const UiLanguageContext = createContext<string | null>(null);

/** Sprache der Kit-Texte; Produkte setzen sie an der Wurzel aus ihrer aktuellen Sprache. */
export const UiLanguageProvider = UiLanguageContext.Provider;

function documentLanguage(): string | null {
  return typeof document === "undefined" ? null : document.documentElement.lang || null;
}

/** Zweistellige Sprache, die das Produkt dem Kit gesetzt hat (Fallback `<html lang>`, sonst Deutsch). */
export function useUiLanguage(): string {
  return (useContext(UiLanguageContext) ?? documentLanguage() ?? "de").slice(0, LANGUAGE_CODE_LENGTH).toLowerCase();
}

export function useUiText(): { text: (key: UiTextKey) => string } {
  const lang = useUiLanguage();
  const dict = uiText[lang] ?? uiTextDe;
  return { text: (key) => dict[key] };
}
